/**
 * TanStack Query factories for second factors and the sign-in policy. The
 * challenge mutations are the login page's; the rest are a signed-in user's
 * own (mfaStatusQuery and friends) or an administrator's (policy, reset).
 */
import { queryOptions, type UseMutationOptions } from '@tanstack/react-query';

import type { ApiClient } from './client';
import type {
  AuthPolicy,
  ConfirmedFactor,
  LoginResult,
  MFAMethod,
  MFARequirement,
  MFAStatus,
  SAMLConnection,
  SAMLDiscovery,
  SAMLDomain,
  SAMLInput,
  SAMLSettings,
  SessionWithRecoveryCodes,
  TOTPEnrollment,
} from './mfaTypes';
import type { Session } from './types';

export const mfaKeys = {
  /** The caller's own factors. */
  status: ['auth', 'mfa'] as const,
  policy: ['auth', 'policy'] as const,
  saml: ['auth', 'saml'] as const,
};

export const mfaStatusQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: mfaKeys.status,
    queryFn: () => api.get<MFAStatus>('/auth/mfa'),
    staleTime: 30_000,
  });

export const authPolicyQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: mfaKeys.policy,
    queryFn: () => api.get<AuthPolicy>('/auth/policy'),
    staleTime: 60_000,
  });

export const samlSettingsQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: mfaKeys.saml,
    queryFn: () => api.get<SAMLSettings>('/auth/saml'),
    staleTime: 60_000,
  });

export const putSAMLMutation = (
  api: ApiClient,
): UseMutationOptions<SAMLConnection, Error, SAMLInput> => ({
  mutationFn: (input) => api.put<SAMLConnection>('/auth/saml', input),
});

export const deleteSAMLMutation = (api: ApiClient): UseMutationOptions<void> => ({
  mutationFn: () => api.delete('/auth/saml'),
});

/** Claims an email domain; it is pending until its TXT record is found. */
export const claimSAMLDomainMutation = (
  api: ApiClient,
): UseMutationOptions<SAMLDomain, Error, { domain: string }> => ({
  mutationFn: (input) => api.post<SAMLDomain>('/auth/saml/domains', input),
});

/** Looks a claimed domain's TXT record up now. */
export const verifySAMLDomainMutation = (
  api: ApiClient,
): UseMutationOptions<SAMLDomain, Error, string> => ({
  mutationFn: (domain) =>
    api.post<SAMLDomain>(`/auth/saml/domains/${encodeURIComponent(domain)}/verify`),
});

/** Drops a domain claim. */
export const removeSAMLDomainMutation = (
  api: ApiClient,
): UseMutationOptions<void, Error, string> => ({
  mutationFn: (domain) => api.delete(`/auth/saml/domains/${encodeURIComponent(domain)}`),
});

/** Finds the identity provider for an email's domain: the login page's SSO
 * button. */
export const samlDiscoverMutation = (
  api: ApiClient,
): UseMutationOptions<SAMLDiscovery, Error, { email: string }> => ({
  mutationFn: (input) => api.post<SAMLDiscovery>('/auth/saml/discover', input),
});

/* --- The login page: answering a challenge ------------------------------- */

export interface LoginInput {
  email: string;
  password: string;
}

/** A password sign-in: a session, or a challenge for a second factor. */
export const loginMutation = (
  api: ApiClient,
): UseMutationOptions<LoginResult, Error, LoginInput> => ({
  mutationFn: (input) => api.post<LoginResult>('/auth/login', input),
});

export type VerifyMFAInput =
  | { mfaToken: string; method: Exclude<MFAMethod, 'webauthn'>; code: string }
  | { mfaToken: string; method: 'webauthn'; credential: unknown };

export const verifyMFAMutation = (
  api: ApiClient,
): UseMutationOptions<Session, Error, VerifyMFAInput> => ({
  mutationFn: (input) => api.post<Session>('/auth/mfa/verify', input),
});

export interface ChallengeTOTPInput {
  mfaToken: string;
  label?: string;
}

/** Starts the authenticator app a policy requires, during the sign-in. */
export const challengeTOTPMutation = (
  api: ApiClient,
): UseMutationOptions<TOTPEnrollment, Error, ChallengeTOTPInput> => ({
  mutationFn: (input) => api.post<TOTPEnrollment>('/auth/mfa/challenge/totp', input),
  gcTime: 0,
});

export interface ConfirmChallengeTOTPInput {
  mfaToken: string;
  factorId: string;
  code: string;
}

export const confirmChallengeTOTPMutation = (
  api: ApiClient,
): UseMutationOptions<SessionWithRecoveryCodes, Error, ConfirmChallengeTOTPInput> => ({
  mutationFn: (input) =>
    api.post<SessionWithRecoveryCodes>('/auth/mfa/challenge/totp/confirm', input),
  gcTime: 0,
});

/** WebAuthnCeremony is the first half of a security-key ceremony: options for
 * navigator.credentials, and the token to send back with the answer. */
export interface WebAuthnCeremony {
  options: unknown;
  token: string;
}

/** Signs in with a security key: the options for the challenge, the browser's
 * assertion, then the verify call with the token the options came with. */
export const securityKeySignInMutation = (
  api: ApiClient,
  assert: (options: unknown) => Promise<unknown>,
): UseMutationOptions<Session, Error, { mfaToken: string }> => ({
  mutationFn: async ({ mfaToken }) => {
    const ceremony = await api.post<WebAuthnCeremony>('/auth/mfa/webauthn/challenge', { mfaToken });
    const credential = await assert(ceremony.options);
    return api.post<Session>('/auth/mfa/verify', {
      mfaToken: ceremony.token,
      method: 'webauthn',
      credential,
    });
  },
});

/** Registers the first security key a policy requires, during the sign-in. */
export const challengeSecurityKeyMutation = (
  api: ApiClient,
  create: (options: unknown) => Promise<unknown>,
): UseMutationOptions<SessionWithRecoveryCodes, Error, { mfaToken: string; label?: string }> => ({
  mutationFn: async ({ mfaToken, label }) => {
    const ceremony = await api.post<WebAuthnCeremony>('/auth/mfa/challenge/webauthn', { mfaToken });
    const credential = await create(ceremony.options);
    return api.post<SessionWithRecoveryCodes>('/auth/mfa/challenge/webauthn/confirm', {
      mfaToken: ceremony.token,
      credential,
      ...(label ? { label } : {}),
    });
  },
  gcTime: 0,
});

/* --- A signed-in user's own factors ---------------------------------------- */

export const beginTOTPMutation = (
  api: ApiClient,
): UseMutationOptions<TOTPEnrollment, Error, { label?: string }> => ({
  mutationFn: (input) => api.post<TOTPEnrollment>('/auth/mfa/totp', input),
  gcTime: 0,
});

export interface ConfirmTOTPInput {
  factorId: string;
  code: string;
}

export const confirmTOTPMutation = (
  api: ApiClient,
): UseMutationOptions<ConfirmedFactor, Error, ConfirmTOTPInput> => ({
  mutationFn: (input) => api.post<ConfirmedFactor>('/auth/mfa/totp/confirm', input),
  gcTime: 0,
});

/** Registers a security key or passkey for the signed-in user. */
export const registerSecurityKeyMutation = (
  api: ApiClient,
  create: (options: unknown) => Promise<unknown>,
): UseMutationOptions<ConfirmedFactor, Error, { label?: string }> => ({
  mutationFn: async ({ label }) => {
    const ceremony = await api.post<WebAuthnCeremony>('/auth/mfa/webauthn/register');
    const credential = await create(ceremony.options);
    return api.post<ConfirmedFactor>('/auth/mfa/webauthn/register/finish', {
      token: ceremony.token,
      credential,
      ...(label ? { label } : {}),
    });
  },
  gcTime: 0,
});

export const removeFactorMutation = (api: ApiClient): UseMutationOptions<void, Error, string> => ({
  mutationFn: (id) => api.delete(`/auth/mfa/${encodeURIComponent(id)}`),
});

export const regenerateRecoveryCodesMutation = (
  api: ApiClient,
): UseMutationOptions<{ recoveryCodes: string[] }> => ({
  mutationFn: () => api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes'),
  gcTime: 0,
});

/* --- Administration --------------------------------------------------------- */

/** Clears another user's factors and ends their sessions. */
export const resetUserMFAMutation = (api: ApiClient): UseMutationOptions<void, Error, string> => ({
  mutationFn: (id) => api.post(`/auth/users/${encodeURIComponent(id)}/mfa/reset`),
});

export const updateAuthPolicyMutation = (
  api: ApiClient,
): UseMutationOptions<AuthPolicy, Error, { mfaRequired: MFARequirement }> => ({
  mutationFn: (input) => api.put<AuthPolicy>('/auth/policy', input),
});

/**
 * otpauthQRCodeQuery renders an otpauth:// URI as an SVG QR code, as a data
 * URL an <img> can show. Dark on light whatever the theme: a scanner needs
 * the contrast. Generated here, in the browser, so the secret never reaches a
 * third-party service; the encoder is loaded only when a QR code is drawn, so
 * it stays out of every startup bundle. Nothing here comes from the server:
 * the cache entry is dropped as soon as the dialog showing it closes.
 */
export const otpauthQRCodeQuery = (uri: string) =>
  queryOptions({
    queryKey: ['otpauth-qr', uri] as const,
    queryFn: async () => {
      const { renderSVG } = await import('uqr');
      return `data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(uri, { border: 2 }))}`;
    },
    staleTime: Infinity,
    gcTime: 0,
  });
